// Combined inventory: one building sold through several PMS listings, priced from a lead listing
// (docs/plans/2026-10-16-combined-inventory.md).
//
// THE SERVER DRAWS THE ROOM MAPPING. Every row, option label, count and sentence on the mapping step
// comes from PreviewRoomMappingMutation; this file only holds the operator's choices between two
// previews, turns them into the request body, and says which rooms still have no choice (a membership
// check on the choices, never arithmetic on a room count or a price).
//
// ONE GLANCE, ONE CLICK ("Optimistic room mapping"). Each room has one choice: the lead room it is the
// same as, or Added room. Rate plans pair by name on save, a room's own link is set aside while it is a
// Same room, and anything left unsettled is named after saving, so the step never refuses what the
// system can handle. A room with no choice yet is the only thing that holds Save.
//
// Vocabulary (locked, §2 rule 1): "Combined inventory", "lead listing", "following listing",
// "Same room", "Added room", "Same room as <lead room>".

import { formatRateSym } from '@/lib/calendar-utils'
import type { SameRoomOffsetDraft, SameRoomSetup } from '@/types/shared'

export type RoomKind = 'same' | 'added'

/** What the operator chose for one room. `kind: null` is no choice yet. */
export interface MappingDecision {
  kind: RoomKind | null
  sameAsRoomTypeId: number | null
}

export type MappingDecisions = Record<number, MappingDecision>

/** One lead room, as every row offers it: "Same as Double · 6 rooms". */
export interface LeadRoomOption {
  id: number
  name: string
  totalUnits: number
  label: string
}

export interface MappingRoomRow {
  id: number
  name: string
  totalUnits: number
  /** "6 rooms", server-worded. */
  roomsLabel: string
  decision: { kind: RoomKind | null; sameAsRoomTypeId: number | null }
}

export interface MappingListing {
  propertyId: number
  name: string
  totalUnits: number
  rooms: MappingRoomRow[]
}

export interface RoomMappingPreview {
  mode: 'join' | 'edit'
  lead: { propertyId: number; name: string; totalUnits: number }
  /** Every lead room, the options of every row in this order. */
  leadRooms: LeadRoomOption[]
  /** The last option of every row: "Added room (not at the lead)". */
  addedOptionLabel: string
  listings: MappingListing[]
  toChooseCount: number
  /** Null while any room has no choice: a count is never quoted before it is true. */
  combinedRoomCount: number | null
  /** "30 rooms in the building", once every room has a choice. */
  countLine: string | null
  ready: boolean
}

/** What the step asks the server about. Ids only; every one is resolved inside the business. */
export interface MappingRequest {
  mode: 'join' | 'edit'
  /** An existing combined inventory (adding a listing, editing a mapping). */
  groupId?: number
  /** Creating one: the hotel that will lead. */
  leadPropertyId?: number
  propertyIds: number[]
}

/** One refused-save sentence, anchored to the room it names when it names one. */
export interface MappingErrorItem {
  roomTypeId: number | null
  message: string
}

/** What a room mapping save did (App::RoomMappingOutcome): one sentence, and its things to check. */
export interface MappingResult {
  headline: string
  /** "1 thing to check", or null when nothing needs a look. */
  checksLabel: string | null
  checksHref: string | null
  /**
   * "Set Same room offsets and rate plans" when the one listing that joined has a Same room, linking to its
   * room mapping page (§3.20); null otherwise.
   */
  offsetsLabel?: string | null
  offsetsHref?: string | null
}

/** "Same room as Double". The one spelling of the label, wherever a twin is named. */
export function sameRoomLabel(twinName: string): string {
  return `Same room as ${twinName}`
}

/** The Select value meaning "Added room". Lead rooms are their ids. */
export const ADDED_CHOICE = 'added'

/** The decisions the step starts from: whatever the server resolved for each room (its suggestion when
 * joining, the saved mapping when editing). */
export function decisionsFromPreview(preview: RoomMappingPreview): MappingDecisions {
  const decisions: MappingDecisions = {}
  for (const listing of preview.listings) {
    for (const room of listing.rooms) {
      decisions[room.id] = { kind: room.decision.kind, sameAsRoomTypeId: room.decision.sameAsRoomTypeId }
    }
  }
  return decisions
}

/** The row's Select value: a lead room's id, ADDED_CHOICE, or '' for no choice yet. */
export function choiceValue(decision: MappingDecision | undefined): string {
  if (decision?.kind === 'added') return ADDED_CHOICE
  if (decision?.kind === 'same' && decision.sameAsRoomTypeId != null) return String(decision.sameAsRoomTypeId)
  return ''
}

/** One room's new choice, from its Select: a lead room's id, or ADDED_CHOICE. */
export function withChoice(decisions: MappingDecisions, roomId: number, value: string): MappingDecisions {
  if (value === ADDED_CHOICE) return { ...decisions, [roomId]: { kind: 'added', sameAsRoomTypeId: null } }
  return { ...decisions, [roomId]: { kind: 'same', sameAsRoomTypeId: Number(value) } }
}

/** True when the room is a Same room of a lead room, or an Added room. */
export function isDecided(decision: MappingDecision | undefined): boolean {
  if (!decision) return false
  return decision.kind === 'added' || (decision.kind === 'same' && decision.sameAsRoomTypeId != null)
}

/** The rooms on the step that still have no choice. */
export function undecidedRoomIds(preview: RoomMappingPreview, decisions: MappingDecisions): number[] {
  return preview.listings.flatMap((listing) => listing.rooms.map((room) => room.id)).filter((id) => !isDecided(decisions[id]))
}

/** "1 room to choose", "3 rooms to choose". */
export function toChooseLabel(count: number): string {
  return count === 1 ? '1 room to choose' : `${count} rooms to choose`
}

/**
 * The room on the same listing that already holds this lead room as its twin, if any: one Same room
 * per lead room per listing, so the Select disables a lead room another row has taken rather than
 * letting the save refuse it.
 */
export function twinHeldBy(
  listing: MappingListing,
  decisions: MappingDecisions,
  twinId: number,
  exceptRoomId: number,
): MappingRoomRow | null {
  return (
    listing.rooms.find(
      (room) =>
        room.id !== exceptRoomId &&
        decisions[room.id]?.kind === 'same' &&
        decisions[room.id]?.sameAsRoomTypeId === twinId,
    ) ?? null
  )
}

/** The request body for the preview and the saves: { room id => { kind, same_as_room_type_id } }. */
export function mappingPayload(
  decisions: MappingDecisions,
): Record<string, { kind: RoomKind | null; same_as_room_type_id: number | null }> {
  const payload: ReturnType<typeof mappingPayload> = {}
  for (const [roomId, decision] of Object.entries(decisions)) {
    payload[roomId] = {
      kind: decision.kind,
      same_as_room_type_id: decision.kind === 'same' ? decision.sameAsRoomTypeId : null,
    }
  }
  return payload
}

/** The preview request body: the ids of the request plus the decisions held so far. */
export function previewBody(request: MappingRequest, decisions: MappingDecisions | null): Record<string, unknown> {
  return {
    mode: request.mode,
    group_id: request.groupId ?? null,
    lead_property_id: request.leadPropertyId ?? null,
    property_ids: request.propertyIds,
    room_mappings: decisions ? mappingPayload(decisions) : {},
  }
}

/**
 * The structured refusal a mapping save answers with (Inertia errors under `roomMapping`), or null
 * when the errors are about something else.
 */
export function parseMappingErrors(raw: unknown): MappingErrorItem[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null
  const items = raw
    .filter((item): item is { roomTypeId?: unknown; message?: unknown } => item != null && typeof item === 'object')
    .filter((item) => typeof item.message === 'string' && item.message !== '')
    .map((item) => ({
      roomTypeId: typeof item.roomTypeId === 'number' && item.roomTypeId > 0 ? item.roomTypeId : null,
      message: item.message as string,
    }))
  return items.length > 0 ? items : null
}

/** "Base Rate Plan copies Base Rate", or "Breakfast is not paired". One rate plan of a Same room. */
export function planPairPhrase(pair: { name: string; partnerName: string | null }): string {
  return pair.partnerName ? `${pair.name} copies ${pair.partnerName}` : `${pair.name} is not paired`
}

/** "Sells at Double's price". What a Same room's price columns say instead of a base rate. */
export function sameRoomPriceNote(twinName: string): string {
  return `Sells at ${twinName}'s price`
}

/** "1 plan not paired", "2 plans not paired". */
export function unpairedPlansLabel(count: number): string {
  return `${count} ${count === 1 ? 'plan' : 'plans'} not paired`
}

/** The chip a Same room whose twin has no price wears: "Not priced: Double has no price yet". */
export function twinNotPricedLabel(twinName: string): string {
  return `Not priced: ${twinName} has no price yet`
}

/** The chip a Same room with no rate plan paired wears. */
export const PLANS_NOT_PAIRED_LABEL = 'Not priced: no rate plan paired'

// ── A Same room's offset from its twin (owner ruling 2026-10-26) ──────────────────────────────────
//
// The one control a Same room has on Rooms Setup. The server stores, judges, reviews and records it;
// these helpers only hold what the operator typed and write it the way the server writes a saved one
// ("+10%", "−£5", "+£7.50"). Formatting and comparison of the typed value, never arithmetic on a price.

const MINUS = '\u2212'

/** The offset Rooms Setup starts from: the saved one, or none, with % as the kind (the % first rule). */
export function sameRoomOffsetDraft(sameAs: Pick<SameRoomSetup, 'offsetType' | 'offsetValue'>): SameRoomOffsetDraft {
  return {
    offsetType: sameAs.offsetType === 'absolute' ? 'absolute' : 'percentage',
    offsetValue: sameAs.offsetValue == null ? '' : String(sameAs.offsetValue),
  }
}

/** True when the typed value is no offset: blank, or zero. The kind alone is never an offset. */
export function isNoOffset(draft: SameRoomOffsetDraft): boolean {
  const raw = draft.offsetValue.trim()
  return raw === '' || Number(raw) === 0
}

/** "+10%", "−£5", "+£7.50", or null for no offset (or a value that is not a number). */
export function sameRoomOffsetLabel(draft: SameRoomOffsetDraft, sym: string): string | null {
  if (isNoOffset(draft)) return null
  const value = Number(draft.offsetValue.trim())
  if (!Number.isFinite(value)) return null
  const sign = value < 0 ? MINUS : '+'
  const size = Math.abs(value)
  return draft.offsetType === 'absolute' ? `${sign}${formatRateSym(size, sym)}` : `${sign}${size}%`
}

/** Whether the staged offset differs from the saved one. No offset equals no offset, whatever the kind. */
export function sameRoomOffsetChanged(draft: SameRoomOffsetDraft, saved: SameRoomOffsetDraft): boolean {
  if (isNoOffset(draft) || isNoOffset(saved)) return isNoOffset(draft) !== isNoOffset(saved)
  return draft.offsetType !== saved.offsetType || Number(draft.offsetValue) !== Number(saved.offsetValue)
}

/** "Double Room sells at Double's price +10%." The dialog's reading of what it is about to stage. */
export function sameRoomOffsetSentence(roomName: string, twinName: string, label: string | null): string {
  return label ? `${roomName} sells at ${twinName}'s price ${label}.` : `${roomName} sells at ${twinName}'s price.`
}
